// GENERATED from messaging-platform/packages/chat-core/src/image-crop.ts. Do not edit here: edit there, then run scripts/sync-chat-core.cjs.
/**
 * Choosing the square of a photo that becomes someone's profile picture (2026-09-20).
 *
 * The maths is here, framework-free and testable; each app draws its own dialog around it (a round window over the
 * picture, drag to move, a slider to zoom) and hands back what the person chose. `squareArea` turns that into a square
 * of the ORIGINAL pixels, clamped so the window is always full of picture; `cropToSquare` cuts it out.
 */

export interface CropChoice {
  /** the natural size of the picture as loaded */
  naturalWidth: number;
  naturalHeight: number;
  /** the side of the square window the person is looking through, in CSS pixels */
  box: number;
  /** 1 fills the window with the whole of the short side; above that is zoomed in */
  zoom: number;
  /** how far the picture has been dragged inside the window, in CSS pixels */
  offsetX: number;
  offsetY: number;
}

export interface CropArea {
  /** the top-left of the square, in the picture's own pixels */
  x: number;
  y: number;
  /** its side, in the picture's own pixels */
  size: number;
}

/** The zoom a dialog should allow: 1 shows as much as possible, 4 is close enough for a face in a group photo. */
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;
/** The side of the square that is stored. Two of these fit a retina avatar at any size chat shows. */
export const AVATAR_SIZE = 256;

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

/**
 * The square of the original picture that the window is showing. Panning past an edge is not possible: the result is
 * clamped, which is why the dialog can let the drag run free and simply re-read this.
 */
export function squareArea(choice: CropChoice): CropArea {
  const { naturalWidth: w, naturalHeight: h, box } = choice;
  if (w <= 0 || h <= 0 || box <= 0) return { x: 0, y: 0, size: Math.max(1, Math.min(w, h)) };
  const zoom = clamp(choice.zoom || 1, MIN_ZOOM, MAX_ZOOM);
  // At zoom 1 the short side exactly fills the window; the scale maps picture pixels to CSS pixels.
  const scale = (box / Math.min(w, h)) * zoom;
  const size = clamp(box / scale, 1, Math.min(w, h));
  // The drag is in CSS pixels and moves the picture, so the window moves the other way.
  const x = clamp(-choice.offsetX / scale + (w - size) / 2, 0, Math.max(0, w - size));
  const y = clamp(-choice.offsetY / scale + (h - size) / 2, 0, Math.max(0, h - size));
  return { x, y, size };
}

/** How far the picture may be dragged before an edge would show, in CSS pixels. The dialog clamps its own drag with this. */
export function panLimit(choice: Pick<CropChoice, 'naturalWidth' | 'naturalHeight' | 'box' | 'zoom'>): { x: number; y: number } {
  const { naturalWidth: w, naturalHeight: h, box } = choice;
  if (w <= 0 || h <= 0 || box <= 0) return { x: 0, y: 0 };
  const scale = (box / Math.min(w, h)) * clamp(choice.zoom || 1, MIN_ZOOM, MAX_ZOOM);
  return { x: Math.max(0, (w * scale - box) / 2), y: Math.max(0, (h * scale - box) / 2) };
}

/**
 * Cuts that square out of the picture, at `out` pixels square. A browser job: it needs a canvas. Returns a JPEG,
 * or a PNG when the picture had one (a screenshot with transparency stays sharp).
 */
export async function cropToSquare(file: Blob, area: CropArea, out = AVATAR_SIZE): Promise<Blob> {
  if (typeof createImageBitmap === 'undefined' || typeof document === 'undefined') return file;
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = out;
    canvas.height = out;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, area.x, area.y, area.size, area.size, 0, 0, out, out);
    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, type, 0.9));
    return blob ?? file;
  } finally {
    bitmap.close();
  }
}
